import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { CheckCircle, CircleNotch, Envelope, Lock, WarningCircle } from "@phosphor-icons/react";
import { supabase } from "@/integrations/supabase/client";
import { getOrderForClaim, claimOrder } from "@/lib/orders.functions";
import { checkEmailHasAccount } from "@/lib/auth.functions";
import bananaLogo from "@/assets/banana-cards-logo.png";

type ClaimSearch = {
  id?: string;
  number?: string;
  email?: string;
  redirect?: string;
};

export const Route = createFileRoute("/orders/claim")({
  head: () => ({
    meta: [
      { title: "Manage your order — banana.cards" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>): ClaimSearch => ({
    id: typeof s.id === "string" ? s.id : undefined,
    number: typeof s.number === "string" ? s.number : undefined,
    email: typeof s.email === "string" ? s.email : undefined,
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
  }),
  component: ClaimPage,
});

type MinimalOrder = {
  id: string;
  order_number: string;
  status: string;
  email: string | null;
  user_id: string | null;
};

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.6 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.5-4.6 2.4-7.5 2.4-5.3 0-9.7-3.4-11.3-8L6.1 32.6C9.5 39.2 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.5 5.5c-.5.4 6.2-4.5 6.2-15.1 0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function ClaimPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const lookup = useServerFn(getOrderForClaim);
  const claim = useServerFn(claimOrder);
  const checkAccount = useServerFn(checkEmailHasAccount);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [order, setOrder] = useState<MinimalOrder | null>(null);

  // Load the order (public lookup: by id, or number+email).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await lookup({
          data: {
            id: search.id ?? null,
            number: search.number ?? null,
            email: search.email ?? null,
          },
        });
        if (cancelled) return;
        if (res?.order) setOrder(res.order as MinimalOrder);
        else setNotFound(true);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.id, search.number, search.email]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-3xl px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src={bananaLogo} alt="banana.cards" className="h-3.5" />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 py-10 sm:py-16">
        {loading && (
          <div className="rounded-2xl bg-muted p-8 text-center">
            <CircleNotch className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          </div>
        )}

        {!loading && notFound && (
          <div className="rounded-2xl bg-muted p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-background text-destructive">
              <WarningCircle className="h-6 w-6" weight="fill" />
            </div>
            <h1 className="font-display text-2xl mb-2">Order not found</h1>
            <p className="text-sm text-muted-foreground mb-6">
              We couldn't find an order matching that link. Please check your
              confirmation email or contact support.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <a
                href="mailto:support@banana.cards"
                className="inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background"
              >
                Contact support
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-5 py-2.5 text-sm font-bold text-foreground"
              >
                Go home
              </Link>
            </div>
          </div>
        )}

        {!loading && order && (
          <ClaimFlow
            order={order}
            redirect={search.redirect}
            claim={claim}
            checkAccount={checkAccount}
            onDone={(orderId) => {
              navigate({
                to: search.redirect && search.redirect.startsWith("/")
                  ? search.redirect
                  : "/orders/$id_/customize",
                params: { id_: orderId },
              } as any);
            }}
          />
        )}
      </main>
    </div>
  );
}

type Mode = "signup" | "signin";

function ClaimFlow({
  order,
  redirect,
  claim,
  checkAccount,
  onDone,
}: {
  order: MinimalOrder;
  redirect?: string;
  claim: ReturnType<typeof useServerFn<typeof claimOrder>>;
  checkAccount: ReturnType<typeof useServerFn<typeof checkEmailHasAccount>>;
  onDone: (orderId: string) => void;
}) {
  const orderEmail = (order.email ?? "").toLowerCase();
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("signup");
  const [hasAccount, setHasAccount] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [claimed, setClaimed] = useState(false);

  async function runClaim() {
    try {
      const res = await claim({ data: { id: order.id } });
      setClaimed(true);
      onDone(res.orderId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not link your order");
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function evaluate(email: string | null) {
      if (cancelled) return;
      setSessionEmail(email);
      setCheckingSession(false);
      if (email && orderEmail && email === orderEmail) {
        await runClaim();
        return;
      }
      if (!email && orderEmail) {
        try {
          const res = await checkAccount({ data: { email: orderEmail } });
          if (cancelled) return;
          setHasAccount(res.hasAccount);
          setMode(res.hasAccount ? "signin" : "signup");
        } catch {
          if (!cancelled) setHasAccount(false);
        }
      }
    }
    (async () => {
      const { data } = await supabase.auth.getUser();
      await evaluate(data.user?.email?.toLowerCase() ?? null);
    })();
    // Re-evaluate on auth changes (Google OAuth returns via URL hash — the
    // initial getUser() call can race with Supabase's hash detection, so we
    // also react to SIGNED_IN events).
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== "SIGNED_IN" && event !== "INITIAL_SESSION" && event !== "USER_UPDATED") return;
      void evaluate(session?.user?.email?.toLowerCase() ?? null);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderEmail]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!orderEmail) {
      setErr("This order has no email attached — please contact support.");
      return;
    }
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setErr(null);
    setWorking(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email: orderEmail,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) {
          if (/already|registered|exists/i.test(error.message)) {
            setMode("signin");
            setErr("An account with this email already exists. Please sign in.");
            return;
          }
          throw error;
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: orderEmail,
          password,
        });
        if (error) throw error;
      }
      await runClaim();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setWorking(false);
    }
  }

  async function switchAccount() {
    setErr(null);
    setWorking(true);
    try {
      await supabase.auth.signOut();
      setSessionEmail(null);
      if (orderEmail) {
        try {
          const res = await checkAccount({ data: { email: orderEmail } });
          setHasAccount(res.hasAccount);
          setMode(res.hasAccount ? "signin" : "signup");
        } catch {
          setHasAccount(false);
        }
      }
    } finally {
      setWorking(false);
    }
  }

  async function signInWithGoogle() {
    setErr(null);
    setWorking(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.href,
          queryParams: { prompt: "select_account" },
        },
      });
      if (error) throw error;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not start Google sign-in");
      setWorking(false);
    }
  }

  if (checkingSession) {
    return (
      <div className="rounded-2xl bg-muted p-8 text-center">
        <CircleNotch className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
      </div>
    );
  }

  if (claimed) {
    return (
      <div className="rounded-2xl bg-muted p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-foreground text-background">
          <CheckCircle className="h-6 w-6" weight="fill" />
        </div>
        <h1 className="font-display text-2xl mb-2">You're in</h1>
        <p className="text-sm text-muted-foreground">
          Taking you to your order…
        </p>
      </div>
    );
  }

  // Logged in with the wrong email.
  if (sessionEmail && sessionEmail !== orderEmail) {
    return (
      <div className="rounded-2xl bg-muted p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-background text-destructive">
          <WarningCircle className="h-6 w-6" weight="fill" />
        </div>
        <h1 className="font-display text-2xl mb-2">Wrong account</h1>
        <p className="text-sm text-muted-foreground mb-6">
          This order is for{" "}
          <span className="font-bold text-foreground">{orderEmail}</span>, but
          you're signed in as{" "}
          <span className="font-bold text-foreground">{sessionEmail}</span>.
          Sign out to continue with the correct account.
        </p>
        {err && (
          <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
        <button
          onClick={switchAccount}
          disabled={working}
          className="inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background disabled:opacity-50"
        >
          {working ? <CircleNotch className="h-4 w-4 animate-spin" /> : "Sign out"}
        </button>
      </div>
    );
  }

  // Not signed in — sign in / sign up.
  return (
    <div className="rounded-2xl bg-muted p-6 sm:p-8">
      <div className="text-center mb-6">
        <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-foreground text-background">
          <CheckCircle className="h-6 w-6" weight="fill" />
        </div>
        <h1 className="font-display text-2xl mb-1">
          {hasAccount ? "Sign in to manage your order" : "Create your account"}
        </h1>
        <p className="text-xs text-muted-foreground">
          Order{" "}
          <span className="font-mono font-bold text-foreground">
            {order.order_number}
          </span>
        </p>
      </div>

      <button
        type="button"
        onClick={signInWithGoogle}
        disabled={working}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-5 py-3 text-sm font-bold text-foreground hover:bg-muted disabled:opacity-50 min-h-[3rem] mb-3"
      >
        <GoogleIcon className="h-5 w-5" />
        Continue with Google
      </button>

      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-muted px-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            or with email
          </span>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            Email
          </span>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5">
            <Envelope className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm truncate">{orderEmail || "—"}</span>
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            Password
          </span>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-border bg-background px-3 focus-within:ring-2 focus-within:ring-foreground/20">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={
                mode === "signup" ? "Create a password (8+ chars)" : "Your password"
              }
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              className="w-full bg-transparent py-2.5 text-sm outline-none"
              required
              minLength={8}
            />
          </div>
        </label>

        {err && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={working}
          className="w-full inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background disabled:opacity-50 min-h-[3rem]"
        >
          {working ? (
            <CircleNotch className="h-5 w-5 animate-spin" />
          ) : mode === "signup" ? (
            "Create account & continue"
          ) : (
            "Sign in & continue"
          )}
        </button>

        <div className="text-center text-xs text-muted-foreground pt-1">
          {mode === "signup" ? (
            <>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  setMode("signin");
                  setErr(null);
                }}
                className="underline font-bold text-foreground"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  setMode("signup");
                  setErr(null);
                }}
                className="underline font-bold text-foreground"
              >
                Create one
              </button>
            </>
          )}
        </div>

        {redirect && (
          <div className="text-center pt-2">
            <Link to="/" className="text-xs text-muted-foreground underline">
              Cancel
            </Link>
          </div>
        )}
      </form>
    </div>
  );
}